import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Button, Spin, message, Space, Card, Alert } from 'antd';
import { 
  ArrowLeftOutlined, 
  DownloadOutlined, 
  BookOutlined,
  PrinterOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../../contexts/AuthContext';
import { getManual } from '../../services/manualService';

const { Title, Text } = Typography;

const ManualDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [manualTitle, setManualTitle] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);

  // useCallback으로 fetchManual을 먼저 선언
  const fetchManual = useCallback(async (manualType: string) => {
    try {
      setLoading(true);
      // 로그인하지 않은 사용자는 역할을 전달하지 않음 (undefined)
      const userRole = user?.role || undefined;
      const data = await getManual(manualType, userRole);
      setContent(data);
      
      // 메뉴얼 제목 설정
      switch (manualType) {
        case 'user':
          setManualTitle(t('manuals.detail.titles.user'));
          break;
        case 'admin':
          setManualTitle(t('manuals.detail.titles.admin'));
          break;
        case 'installation':
          setManualTitle(t('manuals.detail.titles.installation'));
          break;
        default:
          setManualTitle(t('manuals.detail.titles.default'));
      }
    } catch (error) {
      message.error(t('manuals.detail.loadFailed'));
      navigate('/manuals');
    } finally {
      setLoading(false);
    }
  }, [t, navigate, user?.role]);

  // useEffect에서 fetchManual 사용
  useEffect(() => {
    if (id) {
      // 관리자 전용 사용법인 경우 권한 확인
      if (id === 'admin' || id === 'installation') {
        if (!user || user.role !== 'ADMIN') {
          setAccessDenied(true);
          setLoading(false);
          return;
        }
      }
      
      // 일반 사용자 메뉴얼은 로그인하지 않은 사용자도 접근 가능
      setAccessDenied(false);
      fetchManual(id);
    }
  }, [id, user, fetchManual]);

  const handleDownload = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || `http://localhost:${process.env.REACT_APP_BACKEND_PORT || 3000}`}/api/manuals/${id}`);
      const content = await response.text();
      
      const blob = new Blob([content], { type: 'text/markdown' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${id}_manual.md`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      message.success(t('manuals.detail.downloadSuccess'));
    } catch (error) {
      message.error(t('manuals.detail.downloadFailed'));
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (accessDenied) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Alert
          message={t('manuals.detail.accessDenied')}
          description={t('manuals.detail.accessDeniedDesc')}
          type="error"
          showIcon
          style={{ maxWidth: '500px', margin: '0 auto' }}
        />
        <div style={{ marginTop: '24px' }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/manuals')}
          >
            {t('manuals.actions.backToList')}
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>{t('manuals.detail.loading')}</div>
      </div>
    );
  }

  return (
    <div>
      {/* 헤더 */}
      <div style={{ marginBottom: '24px' }}>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate('/manuals')}
              style={{ marginBottom: '16px' }}
            >
              {t('manuals.actions.backToList')}
            </Button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Title level={2}>
                <BookOutlined style={{ marginRight: '8px' }} />
                {manualTitle}
              </Title>
              <Text type="secondary">
                {t('manuals.detail.description')}
              </Text>
            </div>
            <Space>
              <Button 
                icon={<DownloadOutlined />} 
                onClick={handleDownload}
              >
                {t('manuals.actions.download')}
              </Button>
              <Button 
                icon={<PrinterOutlined />} 
                onClick={handlePrint}
              >
                {t('manuals.actions.print')}
              </Button>
            </Space>
          </div>
        </Space>
      </div>

      {/* 사용법 내용 */}
      <Card>
        <div className="manual-content" style={{ 
          lineHeight: '1.8',
          fontSize: '16px'
        }}>
          <ReactMarkdown
            components={{
              h1: ({ children }) => (
                <Title level={1} style={{ 
                  color: '#1890ff', 
                  borderBottom: '2px solid #1890ff',
                  paddingBottom: '10px',
                  marginBottom: '30px'
                }}>
                  {children}
                </Title>
              ),
              h2: ({ children }) => (
                <Title level={2} style={{ 
                  color: '#262626',
                  marginTop: '40px',
                  marginBottom: '20px',
                  borderLeft: '4px solid #1890ff',
                  paddingLeft: '15px'
                }}>
                  {children}
                </Title>
              ),
              h3: ({ children }) => (
                <Title level={3} style={{ 
                  color: '#595959',
                  marginTop: '30px',
                  marginBottom: '15px'
                }}>
                  {children}
                </Title>
              ),
              h4: ({ children }) => (
                <Title level={4} style={{ 
                  color: '#8c8c8c',
                  marginTop: '25px',
                  marginBottom: '10px'
                }}>
                  {children}
                </Title>
              ),
              p: ({ children }) => (
                <p style={{ marginBottom: '15px' }}>{children}</p>
              ),
              ul: ({ children }) => (
                <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>{children}</ul>
              ),
              ol: ({ children }) => (
                <ol style={{ marginBottom: '15px', paddingLeft: '20px' }}>{children}</ol>
              ),
              li: ({ children }) => (
                <li style={{ marginBottom: '5px' }}>{children}</li>
              ),
              code: ({ children, className }) => {
                const isInline = !className;
                if (isInline) {
                  return (
                    <code style={{
                      backgroundColor: '#f6f8fa',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
                      fontSize: '0.9em'
                    }}>
                      {children}
                    </code>
                  );
                }
                return (
                  <pre style={{
                    backgroundColor: '#f6f8fa',
                    padding: '16px',
                    borderRadius: '6px',
                    overflowX: 'auto',
                    margin: '20px 0'
                  }}>
                    <code style={{
                      fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace"
                    }}>
                      {children}
                    </code>
                  </pre>
                );
              },
              blockquote: ({ children }) => (
                <blockquote style={{
                  borderLeft: '4px solid #d9d9d9',
                  margin: '20px 0',
                  padding: '10px 20px',
                  backgroundColor: '#fafafa'
                }}>
                  {children}
                </blockquote>
              ),
              table: ({ children }) => (
                <table style={{
                  borderCollapse: 'collapse',
                  width: '100%',
                  margin: '20px 0'
                }}>
                  {children}
                </table>
              ),
              th: ({ children }) => (
                <th style={{
                  border: '1px solid #d9d9d9',
                  padding: '12px',
                  textAlign: 'left',
                  backgroundColor: '#fafafa',
                  fontWeight: '600'
                }}>
                  {children}
                </th>
              ),
              td: ({ children }) => (
                <td style={{
                  border: '1px solid #d9d9d9',
                  padding: '12px',
                  textAlign: 'left'
                }}>
                  {children}
                </td>
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      </Card>

      {/* 하단 액션 버튼 */}
      <div style={{ marginTop: '24px', textAlign: 'center' }}>
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/manuals')}
          >
            {t('manuals.actions.backToManuals')}
          </Button>
          <Button 
            type="primary"
            icon={<DownloadOutlined />} 
            onClick={handleDownload}
          >
            {t('manuals.actions.downloadManual')}
          </Button>
        </Space>
      </div>

      {/* 인쇄용 스타일 */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @media print {
            .manual-content {
              font-size: 12px !important;
              line-height: 1.4 !important;
            }
            .ant-btn {
              display: none !important;
            }
          }
        `
      }} />
    </div>
  );
};

export default ManualDetail; 